<template>
  <div class="container py-4">
    <h1 class="mb-4">Tabele pogodowe</h1>

    <div class="mb-4">
      <form @submit.prevent="submitCities" class="row g-3 align-items-end">
        <div class="col-12">
          <div class="d-flex align-items-center mb-2">
            <div class="form-label fw-bold mb-0">Wybierz miasta do porównania</div>
          </div>
          <div class="d-flex align-items-center mb-2">
            <input
              id="select-all-cities"
              class="form-check-input me-2"
              type="checkbox"
              :checked="allSelected"
              :indeterminate="indeterminateSelection"
              :disabled="loading"
              @change="toggleAll(($event.target as HTMLInputElement).checked)"
            />
            <label class="form-check-label me-4" for="select-all-cities">Zaznacz wszystkie</label>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="loc in locations" :key="loc.name" class="form-check form-check-inline">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`city-${loc.name}`"
                :value="loc.name"
                v-model="selectedCities"
                :disabled="loading"
              />
              <label class="form-check-label" :for="`city-${loc.name}`">{{ loc.name }}</label>
            </div>
          </div>
        </div>

        <div class="col-md-5">
          <label class="form-label">Nowe miasto</label>
          <input
            type="text"
            class="form-control"
            placeholder="Nazwa miasta"
            v-model="newCityName"
            :disabled="loading"
          />
        </div>
        <div class="col-md-5">
          <label class="form-label">Współrzędne (lat, lon)</label>
          <input
            type="text"
            class="form-control"
            placeholder="Format: 52.1234, 16.1234"
            v-model="newCityCoords"
            :disabled="loading"
          />
        </div>
        <div class="col-md-2 d-grid">
          <button type="button" class="btn btn-primary" @click="addCity" :disabled="loading || !newCityValid">Dodaj</button>
        </div>

        <div class="col-12">
          <button type="submit" class="btn btn-primary" :disabled="loading">Wyślij</button>
        </div>

        <div v-if="formError" class="col-12 alert alert-warning py-2">
          {{ formError }}
        </div>
      </form>
    </div>

    <div v-if="loading" class="alert alert-info" role="status">
      Ładowanie danych pogodowych…
    </div>

    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <template v-if="!loading && !error">
      <div class="text-muted mb-2">Dane pobrane jednorazowo z Open-Meteo dla wybranych miast po naciśnięciu przycisku Wyślij.</div>
      <div class="form-check mb-2">
        <input
          id="show-app-temp"
          class="form-check-input"
          type="checkbox"
          v-model="showApparentTemperature"
        />
        <label class="form-check-label" for="show-app-temp">Pokaż średnią temperaturę odczuwalną</label>
      </div>
      <div class="form-check mb-2">
        <input
          id="color-max-temp"
          class="form-check-input"
          type="checkbox"
          v-model="colorMaxTemperature"
        />
        <label class="form-check-label" for="color-max-temp">Koloruj maksymalną temperaturę w tabeli dziennej</label>
      </div>
      <div class="form-check mb-2">
        <input
          id="color-avg-temp"
          class="form-check-input"
          type="checkbox"
          v-model="colorAverageTemperature"
        />
        <label class="form-check-label" for="color-avg-temp">Koloruj średnią temperaturę w tabeli dziennej</label>
      </div>
      <div class="form-check mb-4">
        <input
          id="show-business-hours-average"
          class="form-check-input"
          type="checkbox"
          v-model="showBusinessHoursAverage"
        />
        <label class="form-check-label" for="show-business-hours-average">Pokaż średnią z godzin 10:00 - 18:00</label>
      </div>

      <section class="mb-5" v-if="showTables">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <h2 class="h5 mb-0">Średnie dzienne</h2>
          <input
            v-model="dailyFilter"
            type="text"
            class="form-control form-control-sm"
            style="max-width: 220px;"
            placeholder="Regex JS, np. -06-|-07-"
          />
        </div>
        <div class="table-responsive sticky-table">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle sticky-header">
              <tr>
                <th class="align-middle">Dzień</th>
                <th v-for="loc in filteredLocations" :key="loc.name" class="px-2 text-center">
                  <div>{{ loc.name }}<br /><small>{{ getSummaryLabel() }}</small></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredDailyRows" :key="row.key">
                <td>{{ row.key }}</td>
                <td v-for="loc in filteredLocations" :key="loc.name" class="text-end">
                  <template v-if="row.values[loc.name]">
                    <!-- Temperatura średnia -->
                    <span v-if="colorAverageTemperature && !showBusinessHoursAverage" :style="getMaxTemperatureStyle(row.values[loc.name].temp)" class="fw-bold">{{ formatValue(row.values[loc.name].temp) }}</span>
                    <span v-else>{{ formatValue(row.values[loc.name].temp) }}</span>
                    <!-- Średnia 10-18 -->
                    <template v-if="showBusinessHoursAverage">
                      /
                      <span v-if="colorAverageTemperature" :style="getMaxTemperatureStyle(row.values[loc.name].businessHoursAvg)" class="fw-bold">{{ formatValue(row.values[loc.name].businessHoursAvg) }}</span>
                      <span v-else>{{ formatValue(row.values[loc.name].businessHoursAvg) }}</span>
                    </template>
                    <!-- Temperatura odczuwalna -->
                    <template v-if="showApparentTemperature">
                      /{{ formatValue(row.values[loc.name].app) }}
                    </template>
                    <!-- Temperatura maksymalna -->
                    /
                    <span v-if="colorMaxTemperature" :style="getMaxTemperatureStyle(row.values[loc.name].max)" class="fw-bold">{{ formatValue(row.values[loc.name].max) }}</span>
                    <span v-else>{{ formatValue(row.values[loc.name].max) }}</span>
                  </template>
                  <template v-else>
                    n/a
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="mb-5" v-if="showTables">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <h2 class="h5 mb-0">Średnie miesięczne</h2>
          <input
            v-model="monthlyFilter"
            type="text"
            class="form-control form-control-sm"
            style="max-width: 220px;"
            placeholder="Regex JS, np. -06-|-07-"
          />
        </div>
        <div class="table-responsive sticky-table">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle sticky-header">
              <tr>
                <th class="align-middle">Miesiąc</th>
                <th v-for="loc in filteredLocations" :key="loc.name" class="px-2 text-center">
                  <div>{{ loc.name }}<br /><small>{{ getSummaryLabel() }}</small></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredMonthlyRows" :key="row.key">
                <td>{{ row.key }}</td>
                <td v-for="loc in filteredLocations" :key="loc.name" class="text-end">
                  <template v-if="row.values[loc.name]">
                    {{ formatValue(row.values[loc.name].temp) }}{{ showBusinessHoursAverage ? '/' + formatValue(row.values[loc.name].businessHoursAvg) : '' }}{{ showApparentTemperature ? '/' + formatValue(row.values[loc.name].app) : '' }}/{{ formatValue(row.values[loc.name].max) }}
                  </template>
                  <template v-else>
                    n/a
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="mb-5" v-if="showTables">
        <h2 class="h5">Podsumowanie roczne</h2>
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle">
              <tr>
                <th class="align-middle">Rok</th>
                <th v-for="loc in filteredLocations" :key="loc.name" class="px-2 text-center">
                  <div>{{ loc.name }}<br /><small>{{ getSummaryLabel() }}</small></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in yearlyRows" :key="row.key">
                <td>{{ row.key }}</td>
                <td v-for="loc in filteredLocations" :key="loc.name" class="text-end">
                  <template v-if="row.values[loc.name]">
                    {{ formatValue(row.values[loc.name].temp) }}{{ showBusinessHoursAverage ? '/' + formatValue(row.values[loc.name].businessHoursAvg) : '' }}{{ showApparentTemperature ? '/' + formatValue(row.values[loc.name].app) : '' }}/{{ formatValue(row.values[loc.name].max) }}
                  </template>
                  <template v-else>
                    n/a
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="mb-5" v-if="showTables">
        <h2 class="h5">Średnie miesięczne ze wszystkich lat</h2>
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle">
              <tr>
                <th class="align-middle">Średnia za</th>
                <th v-for="loc in filteredLocations" :key="loc.name" class="px-2 text-center">
                  <div>{{ loc.name }}<br /><small>{{ getSummaryLabel() }}</small></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in monthProfileRows" :key="row.key">
                <td>{{ row.key }}</td>
                <td v-for="loc in filteredLocations" :key="loc.name" class="text-end">
                  <template v-if="row.values[loc.name]">
                    {{ formatValue(row.values[loc.name].temp) }}{{ showBusinessHoursAverage ? '/' + formatValue(row.values[loc.name].businessHoursAvg) : '' }}{{ showApparentTemperature ? '/' + formatValue(row.values[loc.name].app) : '' }}/{{ formatValue(row.values[loc.name].max) }}
                  </template>
                  <template v-else>
                    n/a
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="mb-5" v-if="showTables">
        <h2 class="h5">Podsumowanie całego okresu</h2>
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle">
              <tr>
                <th>Okres</th>
                <th v-for="loc in filteredLocations" :key="loc.name" class="text-center">
                  <div>{{ loc.name }}<br /><small>{{ getSummaryLabel() }}</small></div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ reportPeriod }}</td>
                <td v-for="loc in filteredLocations" :key="loc.name" class="text-end">
                  <template v-if="periodRow.values[loc.name]">
                    {{ formatValue(periodRow.values[loc.name].temp) }}{{ showBusinessHoursAverage ? '/' + formatValue(periodRow.values[loc.name].businessHoursAvg) : '' }}{{ showApparentTemperature ? '/' + formatValue(periodRow.values[loc.name].app) : '' }}/{{ formatValue(periodRow.values[loc.name].max) }}
                  </template>
                  <template v-else>
                    n/a
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="showTables">
        <h2 class="h5">Współrzędne zwrócone przez API</h2>
        <div class="table-responsive">
          <table class="table table-bordered table-striped table-sm align-middle mb-0">
            <thead class="table-light text-center align-middle">
              <tr>
                <th>Lokalizacja</th>
                <th>Szerokość (API)</th>
                <th>Długość (API)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="coord in apiCoordinates" :key="coord.name">
                <td>{{ coord.name }}</td>
                <td>{{ coord.latitude.toFixed(4) }}</td>
                <td>{{ coord.longitude.toFixed(4) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw, watch } from 'vue';
import axios from 'axios';

interface LocationEntry {
  name: string;
  lat: number;
  lon: number;
}

interface LocationValues {
  temp: number;
  app: number;
  max: number;
  businessHoursAvg: number;
}

interface RowEntry {
  key: string;
  values: Record<string, LocationValues>;
}

export default defineComponent({
  data() {
    return {
      loading: false as boolean,
      error: '' as string,
      locations: [
        { name: 'Poznań', lat: 52.408284, lon: 16.92873 },
        { name: 'Chojnice', lat: 53.695059, lon: 17.561315 },
        { name: 'Toruń', lat: 53.011375, lon: 18.605433 },
        { name: 'Kwiejce', lat: 52.756659, lon: 15.99472 },
        { name: 'Gdańsk', lat: 54.34415, lon: 18.547668 },
        { name: 'Wrocław', lat: 51.109019, lon: 17.036362 },
        { name: 'Jaszczerek', lat: 53.687762, lon: 18.521576 },
        { name: 'Nowy Tomyśl', lat: 52.315615, lon: 16.122437 },
        { name: 'Przesieki', lat: 53.018064, lon: 15.989709 },
      ] as LocationEntry[],
      dailyRows: [] as RowEntry[],
      monthlyRows: [] as RowEntry[],
      yearlyRows: [] as RowEntry[],
      monthProfileRows: [] as RowEntry[],
      periodRow: { key: '', values: {} as Record<string, LocationValues> } as RowEntry,
      reportPeriod: '' as string,
      apiCoordinates: [] as Array<{ name: string; latitude: number; longitude: number }> ,
      selectedCities: [] as string[],
      showApparentTemperature: false as boolean,
      colorMaxTemperature: false as boolean,
      colorAverageTemperature: false as boolean,
      showBusinessHoursAverage: false as boolean,
      rawWeatherResults: [] as Array<any>,
      rawSelectedLocations: [] as LocationEntry[],
      dailyFilter: '' as string,
      monthlyFilter: '' as string,
      newCityName: '' as string,
      newCityCoords: '' as string,
      formError: '' as string,
      showTables: false as boolean,
    };
  },
  created() {
    this.selectedCities = this.locations.map((loc) => loc.name);
  },
  watch: {
    showBusinessHoursAverage() {
      if (this.rawWeatherResults.length > 0) {
        this.rebuildWeatherRows();
      }
    },
  },
  computed: {
    filteredLocations() {
      return this.locations.filter((loc) => this.selectedCities.includes(loc.name));
    },
    filteredDailyRows() {
      const query = this.dailyFilter.trim();
      if (!query) {
        return this.dailyRows;
      }
      try {
        const regex = new RegExp(query, 'i');
        return this.dailyRows.filter((row) => regex.test(row.key));
      } catch {
        return [];
      }
    },
    filteredMonthlyRows() {
      const query = this.monthlyFilter.trim();
      if (!query) {
        return this.monthlyRows;
      }
      try {
        const regex = new RegExp(query, 'i');
        return this.monthlyRows.filter((row) => regex.test(row.key));
      } catch {
        return [];
      }
    },
    allSelected() {
      return this.selectedCities.length > 0 && this.selectedCities.length === this.locations.length;
    },
    indeterminateSelection() {
      return this.selectedCities.length > 0 && this.selectedCities.length < this.locations.length;
    },
    newCityValid() {
      const name = this.newCityName.trim();
      const coords = this.newCityCoords.trim();
      if (!name || !coords) {
        return false;
      }
      const parts = coords.split(',').map((part) => part.trim());
      if (parts.length !== 2) {
        return false;
      }
      const lat = Number(parts[0]);
      const lon = Number(parts[1]);
      return Number.isFinite(lat) && Number.isFinite(lon);
    },
  },
  methods: {
    addCity() {
      this.formError = '';
      const name = this.newCityName.trim();
      const coords = this.newCityCoords.trim();
      if (!name) {
        this.formError = 'Podaj nazwę miasta.';
        return;
      }
      if (!coords) {
        this.formError = 'Podaj współrzędne w formacie lat, lon.';
        return;
      }
      const parts = coords.split(',').map((part) => part.trim());
      if (parts.length !== 2) {
        this.formError = 'Współrzędne muszą mieć format lat, lon.';
        return;
      }
      const lat = Number(parts[0]);
      const lon = Number(parts[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        this.formError = 'Współrzędne muszą być prawidłowymi liczbami.';
        return;
      }
      if (this.locations.some((loc) => loc.name.toLowerCase() === name.toLowerCase())) {
        this.formError = 'Miasto o takiej nazwie już istnieje.';
        return;
      }

      const shouldSelectNewCity = this.locations.length > 0 && this.locations.every((loc) => this.selectedCities.includes(loc.name));
      this.locations = [...this.locations, { name, lat, lon }];
      if (shouldSelectNewCity) {
        this.selectedCities = [...this.selectedCities, name];
      }
      this.newCityName = '';
      this.newCityCoords = '';
      this.showTables = false;
    },
    submitCities() {
      this.formError = '';
      if (this.selectedCities.length === 0) {
        this.formError = 'Wybierz przynajmniej jedno miasto.';
        return;
      }
      this.showTables = false;
      this.loading = true;
      this.error = '';
      this.fetchWeather();
    },
    toggleAll(checked: boolean) {
      if (checked) {
        this.selectedCities = this.locations.map((loc) => loc.name);
      } else {
        this.selectedCities = [];
      }
    },
    getApiErrorMessage(error: any) {
      if (!error) {
        return '';
      }

      const payload = error?.response?.data;

      if (typeof payload === 'string' && payload.trim()) {
        return payload.trim();
      }

      if (payload && typeof payload === 'object') {
        const candidate = [payload.message, payload.error, payload.reason, payload.detail, payload.error_message, payload.msg]
          .find((value) => typeof value === 'string' && value.trim());

        if (candidate) {
          return candidate.trim();
        }
      }

      if (error?.message) {
        if (error.message.includes('Network Error')) {
          return 'Brak połączenia z serwerem API.';
        }
        if (error.message.includes('timeout')) {
          return 'Przekroczono czas oczekiwania na odpowiedź API.';
        }
        return error.message;
      }

      if (error?.response?.status) {
        const statusText = error.response.statusText || 'błąd odpowiedzi';
        return `API zwróciło błąd ${error.response.status} (${statusText}).`;
      }

      return '';
    },
    formatValue(value: number) {
      return Number.isFinite(value) ? value.toFixed(1) : 'n/a';
    },
    getWarsawDateTimeParts(timeString: string) {
      const date = new Date(timeString);
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Warsaw',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = formatter.formatToParts(date);
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      return {
        localDate: `${values.year}-${values.month}-${values.day}`,
        hour: Number(values.hour),
      };
    },
    getSummaryLabel() {
      if (this.showBusinessHoursAverage && this.showApparentTemperature) {
        return 'Śr/Sr10-18/Odc/Max';
      }
      if (this.showBusinessHoursAverage) {
        return 'Śr/Sr10-18/Max';
      }
      if (this.showApparentTemperature) {
        return 'Śr/Odc/Max';
      }
      return 'Śr/Max';
    },
    getMaxTemperatureStyle(value: number) {
      if (!Number.isFinite(value)) {
        return {};
      }
      if (value >= 30) {
        return { color: 'rgb(139, 0, 0)' };
      }
      if (value >= 25) {
        return { color: 'rgb(220, 53, 69)' };
      }
      if (value >= 22) {
        return { color: 'rgb(255, 140, 0)' };
      }
      if (value >= 20) {
        return { color: 'rgb(255, 193, 7)' };
      }
      if (value >= 18) {
        return { color: 'rgb(40, 167, 69)' };
      }
      return { color: 'rgb(0, 123, 255)' };
    },
    rebuildWeatherRows() {
      const locationResults = this.rawWeatherResults;
      const selectedLocations = this.rawSelectedLocations;

      if (!Array.isArray(locationResults) || locationResults.length === 0 || selectedLocations.length === 0) {
        return;
      }

      this.apiCoordinates = markRaw(locationResults.map((result: any, index: number) => ({
        name: selectedLocations[index]?.name ?? `Lokalizacja ${index + 1}`,
        latitude: Number(result.latitude),
        longitude: Number(result.longitude),
      }))) as Array<{ name: string; latitude: number; longitude: number }>;

      const times = locationResults[0]?.hourly?.time as string[];
      const temperatureValues = locationResults.map((result: any) => result.hourly.temperature_2m as number[]);
      const apparentValues = locationResults.map((result: any) => result.hourly.apparent_temperature as number[]);

      const dailyMap: Record<string, Record<string, { sumTemp: number; sumApp: number; count: number; sumBusinessHoursTemp: number; businessHoursCount: number; maxTemp: number }>> = {};

      times.forEach((timeString, idx) => {
        const { localDate, hour } = this.getWarsawDateTimeParts(timeString);
        const isBusinessHours = hour >= 10 && hour <= 18;

        const dateKey = localDate;
        if (!dailyMap[dateKey]) {
          dailyMap[dateKey] = {};
          selectedLocations.forEach((loc) => {
            dailyMap[dateKey][loc.name] = { sumTemp: 0, sumApp: 0, count: 0, sumBusinessHoursTemp: 0, businessHoursCount: 0, maxTemp: Number.NEGATIVE_INFINITY };
          });
        }

        selectedLocations.forEach((loc, locIdx) => {
          const temp = Number(temperatureValues[locIdx]?.[idx]);
          const app = Number(apparentValues[locIdx]?.[idx]);
          const bucket = dailyMap[dateKey][loc.name];

          if (Number.isFinite(temp)) {
            bucket.sumTemp += temp;
            bucket.count += 1;
            bucket.maxTemp = Math.max(bucket.maxTemp, temp);
            if (isBusinessHours) {
              bucket.sumBusinessHoursTemp += temp;
              bucket.businessHoursCount += 1;
            }
          }
          if (Number.isFinite(app)) {
            bucket.sumApp += app;
          }
        });
      });

      const dailyEntries = Object.entries(dailyMap).sort(([a], [b]) => a.localeCompare(b));
      this.dailyRows = markRaw(dailyEntries.map(([day, values]) => ({
        key: day,
        values: Object.fromEntries(
          selectedLocations.map((loc) => {
            const bucket = values[loc.name];
            const count = bucket.count || 1;
            return [loc.name, {
              temp: bucket.sumTemp / count,
              app: bucket.sumApp / count,
              max: bucket.maxTemp === Number.NEGATIVE_INFINITY ? NaN : bucket.maxTemp,
              businessHoursAvg: bucket.businessHoursCount > 0 ? bucket.sumBusinessHoursTemp / bucket.businessHoursCount : NaN,
            }];
          })
        ),
      }))) as RowEntry[];

      const aggregation = (groupKey: (day: string) => string, labelLookup?: Record<string, string>) => {
        const grouped: Record<string, Record<string, { sumTemp: number; sumApp: number; sumMax: number; sumBusinessHoursAvg: number; businessHoursAvgCount: number; count: number }>> = {};

        this.dailyRows.forEach((row) => {
          const group = groupKey(row.key);
          if (!grouped[group]) {
            grouped[group] = {};
            selectedLocations.forEach((loc) => {
              grouped[group][loc.name] = { sumTemp: 0, sumApp: 0, sumMax: 0, sumBusinessHoursAvg: 0, businessHoursAvgCount: 0, count: 0 };
            });
          }

          selectedLocations.forEach((loc) => {
            const values = row.values[loc.name];
            const groupBucket = grouped[group][loc.name];
            groupBucket.sumTemp += values.temp;
            groupBucket.sumApp += values.app;
            groupBucket.sumMax += values.max;
            if (Number.isFinite(values.businessHoursAvg)) {
              groupBucket.sumBusinessHoursAvg += values.businessHoursAvg;
              groupBucket.businessHoursAvgCount += 1;
            }
            groupBucket.count += 1;
          });
        });

        return Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([group, values]) => ({
            key: labelLookup?.[group] ?? group,
            values: Object.fromEntries(
              selectedLocations.map((loc) => {
                const bucket = values[loc.name];
                const count = bucket.count || 1;
                return [loc.name, {
                  temp: bucket.sumTemp / count,
                  app: bucket.sumApp / count,
                  max: bucket.sumMax / count,
                  businessHoursAvg: bucket.businessHoursAvgCount > 0 ? bucket.sumBusinessHoursAvg / bucket.businessHoursAvgCount : NaN,
                }];
              })
            ),
          }));
      };

      const monthLabels: Record<string, string> = {
        '01': 'Styczeń',
        '02': 'Luty',
        '03': 'Marzec',
        '04': 'Kwiecień',
        '05': 'Maj',
        '06': 'Czerwiec',
        '07': 'Lipiec',
        '08': 'Sierpień',
        '09': 'Wrzesień',
        '10': 'Październik',
        '11': 'Listopad',
        '12': 'Grudzień',
      };

      this.monthlyRows = markRaw(aggregation((day) => day.slice(0, 7))) as RowEntry[];
      this.yearlyRows = markRaw(aggregation((day) => day.slice(0, 4))) as RowEntry[];
      this.monthProfileRows = markRaw(aggregation((day) => day.slice(5, 7), monthLabels)) as RowEntry[];

      const totalBucket: Record<string, { sumTemp: number; sumApp: number; sumMax: number; sumBusinessHoursAvg: number; businessHoursAvgCount: number; count: number }> = {};
      selectedLocations.forEach((loc) => {
        totalBucket[loc.name] = { sumTemp: 0, sumApp: 0, sumMax: 0, sumBusinessHoursAvg: 0, businessHoursAvgCount: 0, count: 0 };
      });

      this.dailyRows.forEach((row) => {
        selectedLocations.forEach((loc) => {
          const values = row.values[loc.name];
          const bucket = totalBucket[loc.name];
          bucket.sumTemp += values.temp;
          bucket.sumApp += values.app;
          bucket.sumMax += values.max;
          if (Number.isFinite(values.businessHoursAvg)) {
            bucket.sumBusinessHoursAvg += values.businessHoursAvg;
            bucket.businessHoursAvgCount += 1;
          }
          bucket.count += 1;
        });
      });

      const yearStart = this.dailyRows[0]?.key.slice(0, 4) ?? '';
      const yearEnd = this.dailyRows[this.dailyRows.length - 1]?.key.slice(0, 4) ?? '';
      this.reportPeriod = yearStart && yearEnd ? `${yearStart}-${yearEnd}` : '';

      this.periodRow = markRaw({
        key: this.reportPeriod,
        values: Object.fromEntries(
          selectedLocations.map((loc) => {
            const bucket = totalBucket[loc.name];
            const count = bucket.count || 1;
            return [loc.name, {
              temp: bucket.sumTemp / count,
              app: bucket.sumApp / count,
              max: bucket.sumMax / count,
              businessHoursAvg: bucket.businessHoursAvgCount > 0 ? bucket.sumBusinessHoursAvg / bucket.businessHoursAvgCount : NaN,
            }];
          })
        ),
      }) as RowEntry;
    },
    async fetchWeather() {
      try {
        const endDate = new Date();
        endDate.setUTCDate(endDate.getUTCDate() - 2);
        const endString = endDate.toISOString().slice(0, 10);

        const selectedLocations = this.locations.filter((loc) => this.selectedCities.includes(loc.name));
        const params = {
          latitude: selectedLocations.map((location) => location.lat),
          longitude: selectedLocations.map((location) => location.lon),
          start_date: '2015-01-01',
          end_date: endString,
          hourly: ['temperature_2m', 'apparent_temperature'],
          timezone: 'UTC',
        };

        const response = await axios.get('https://archive-api.open-meteo.com/v1/archive', {
          params,
          paramsSerializer: (paramsObj: any) => {
            const searchParams = new URLSearchParams();
            Object.entries(paramsObj).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                value.forEach((item) => searchParams.append(key, item.toString()));
              } else {
                searchParams.append(key, value.toString());
              }
            });
            return searchParams.toString();
          },
        });

        const locationResults = response.data as Array<any>;
        if (!Array.isArray(locationResults) || locationResults.length === 0) {
          const payloadMessage = this.getApiErrorMessage({ response: { data: response.data } });
          throw new Error(payloadMessage || 'Odpowiedź API nie zawiera danych dla wybranych miast.');
        }

        this.rawWeatherResults = locationResults;
        this.rawSelectedLocations = selectedLocations;
        this.rebuildWeatherRows();
        this.showTables = true;
      } catch (fetchError: any) {
        const apiMessage = this.getApiErrorMessage(fetchError);
        this.error = apiMessage || fetchError?.message || 'Błąd pobierania danych';
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
<style scoped>
.sticky-table {
  max-height: 60vh;
  overflow: auto;
}
.sticky-table thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}
</style>